import { useMemo, useState } from "react";
import axios from "axios";
import { Save, UserPlus, X } from "lucide-react";
import { API } from "../lib/apiClient";
import { Modal } from "./common";
import { LeadFields, emptyLead } from "./LeadModal";
import { MODES, todayStr } from "../lib/appt";

const trLow = (s) => (s || "").toLocaleLowerCase("tr");

function PersonPicker({ people, value, onPick, onNew }) {
  const [q, setQ] = useState(value?.customerName || "");
  const [open, setOpen] = useState(false);
  const shown = people.filter((p) => trLow(p.name).includes(trLow(q)) || (p.phone || "").includes(q)).slice(0, 8);
  return (
    <div className="field appt-picker">
      <span>Müşteri *</span>
      <div className="appt-picker-row">
        <input value={q} placeholder="Müşteri veya aday ara…" onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); onPick(null); }} data-testid="appt-customer-search" />
        <button type="button" className="outline-button" onClick={onNew} data-testid="appt-new-lead-button"><UserPlus size={14} /> Yeni Müşteri</button>
      </div>
      {open && shown.length > 0 && (
        <ul className="appt-picker-menu" data-testid="appt-customer-menu">
          {shown.map((p) => (
            <li key={p.id}><button type="button" onMouseDown={() => { onPick(p); setQ(p.name); setOpen(false); }} data-testid={`appt-pick-${p.id}`}>
              <strong>{p.name}</strong><span>{p.phone || "—"}</span><em className={p.kind}>{p.kind === "lead" ? "Aday" : "Müşteri"}</em>
            </button></li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function AppointmentModal({ initial, customers, leads, onSaved, onLeadCreated, onClose }) {
  const people = useMemo(() => [
    ...customers.map((c) => ({ ...c, kind: "customer" })),
    ...leads.map((l) => ({ ...l, kind: "lead" })),
  ], [customers, leads]);
  const [form, setForm] = useState(() => ({ subject: "", date: todayStr(), time: "10:00", mode: "Yüz Yüze", note: "", ...(initial || {}) }));
  const [newLead, setNewLead] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const pick = (p) => setForm((f) => (p ? { ...f, customerId: p.id, customerKind: p.kind, customerName: p.name, phone: p.phone || "" } : { ...f, customerId: "", customerName: "" }));
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    let body = { ...form };
    setBusy(true);
    try {
      if (newLead) {
        if (!newLead.name.trim()) throw new Error("Yeni adayın adını yazın");
        const { data: lead } = await axios.post(`${API}/leads`, { ...newLead, source: "Randevu" });
        onLeadCreated(lead);
        body = { ...body, customerId: lead.id, customerKind: "lead", customerName: lead.name, phone: lead.phone };
      }
      if (!body.customerName) throw new Error("Müşteri seçin veya yeni aday ekleyin");
      const { data } = body.id ? await axios.put(`${API}/appointments/${body.id}`, body) : await axios.post(`${API}/appointments`, body);
      onSaved(data);
    } catch (e2) { setErr(e2?.response?.data?.detail || e2.message); } finally { setBusy(false); }
  };
  return (
    <Modal title={initial?.id ? "Randevuyu düzenle" : "Yeni Randevu"} onClose={onClose}>
      <form onSubmit={submit} data-testid="appointment-form">
        {newLead ? (
          <div className="appt-newlead" data-testid="appt-newlead-box">
            <div className="appt-newlead-head"><strong>Yeni potansiyel müşteri adayı</strong>
              <button type="button" className="icon-button" onClick={() => setNewLead(null)} data-testid="appt-newlead-cancel"><X size={16} /></button></div>
            <LeadFields form={newLead} setForm={setNewLead} />
          </div>
        ) : <PersonPicker people={people} value={form} onPick={pick} onNew={() => setNewLead(emptyLead())} />}
        <label className="field full"><span>Konu</span>
          <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Örn. Kasko yenileme görüşmesi" data-testid="appt-subject-input" /></label>
        <div className="form-grid appt-when">
          <label className="field"><span>Tarih *</span><input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="appt-date-input" /></label>
          <label className="field"><span>Saat *</span><input type="time" required step="300" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="appt-time-input" /></label>
        </div>
        <div className="field full"><span>Görüşme şekli</span>
          <div className="segmented appt-modes" data-testid="appt-mode">
            {MODES.map((m) => <button key={m} type="button" className={form.mode === m ? "active" : ""} onClick={() => setForm({ ...form, mode: m })} data-testid={`appt-mode-${m.replace(" ", "-")}`}>{m}</button>)}
          </div>
        </div>
        <label className="field full"><span>Not (opsiyonel)</span>
          <textarea rows="3" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="appt-note-input" /></label>
        {err && <div className="auth-error" data-testid="appt-error">{err}</div>}
        <div className="modal-actions">
          <button type="button" className="outline-button" onClick={onClose} data-testid="appt-cancel-button">Vazgeç</button>
          <button type="submit" className="primary-button" disabled={busy} data-testid="appt-save-button"><Save size={15} /> Kaydet</button>
        </div>
      </form>
    </Modal>
  );
}
